import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  CalendarClock,
  BarChart3,
  ChefHat,
  ClipboardList,
  CreditCard,
  HandCoins,
  LayoutGrid,
  ListTree,
  LogOut,
  Menu as MenuIcon,
  PackageCheck,
  Printer,
  Settings as SettingsIcon,
  SlidersHorizontal,
  Table2,
  UtensilsCrossed,
  Users as UsersIcon,
  Wine,
  X,
} from 'lucide-react';
import type { Permission } from '@sebastian/shared';
import { useAuth } from '../lib/auth';
import { useSettings } from '../hooks/queries';
import { useNow } from '../hooks/useNow';
import { formatClock } from '../lib/format';

interface NavEntry {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  permission: Permission;
  group: 'floor' | 'admin';
}

const NAV: NavEntry[] = [
  { to: '/pos', label: 'Floor', icon: LayoutGrid, permission: 'pos:operate', group: 'floor' },
  { to: '/bookings', label: 'Bookings', icon: CalendarClock, permission: 'pos:operate', group: 'floor' },
  { to: '/kitchen', label: 'Kitchen', icon: ChefHat, permission: 'kds:kitchen', group: 'floor' },
  { to: '/bar', label: 'Bar', icon: Wine, permission: 'kds:bar', group: 'floor' },
  { to: '/payments', label: 'Payments', icon: CreditCard, permission: 'payments:process', group: 'floor' },
  { to: '/orders', label: 'History', icon: ClipboardList, permission: 'orders:history', group: 'floor' },
  { to: '/print-station', label: 'Print station', icon: Printer, permission: 'print:station', group: 'floor' },
  { to: '/stock', label: 'In stock', icon: PackageCheck, permission: 'admin:menu', group: 'admin' },
  { to: '/reports', label: 'Reports', icon: BarChart3, permission: 'reports:read', group: 'admin' },
  { to: '/giveaways', label: 'Giveaways', icon: HandCoins, permission: 'reports:read', group: 'admin' },
  { to: '/menu', label: 'Menu', icon: UtensilsCrossed, permission: 'admin:menu', group: 'admin' },
  { to: '/categories', label: 'Categories', icon: ListTree, permission: 'admin:menu', group: 'admin' },
  { to: '/modifiers', label: 'Modifiers', icon: SlidersHorizontal, permission: 'admin:menu', group: 'admin' },
  { to: '/tables', label: 'Tables', icon: Table2, permission: 'admin:tables', group: 'admin' },
  { to: '/users', label: 'Staff', icon: UsersIcon, permission: 'admin:users', group: 'admin' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, permission: 'admin:settings', group: 'admin' },
];

/** The Sebastian wordmark; the uploaded logo replaces the monogram when set. */
export function Brand({ compact = false }: { compact?: boolean }) {
  const { data: settings } = useSettings();
  const name = settings?.restaurantName ?? 'Sebastian';

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-accent/50 bg-accent/10">
        {settings?.logoUrl ? (
          <img src={settings.logoUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="font-display text-lg font-semibold text-accent-strong">S</span>
        )}
      </div>
      {compact ? null : (
        <div className="min-w-0">
          <div className="truncate font-display text-lg leading-tight tracking-[0.18em] text-ink uppercase">
            {name}
          </div>
          <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-ink-faint">
            Point of Sale
          </div>
        </div>
      )}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut, allows } = useAuth();
  const location = useLocation();
  const now = useNow(15_000);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Navigating on a tablet should always close the slide-over.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  const visible = NAV.filter((entry) => allows(entry.permission));
  const floor = visible.filter((entry) => entry.group === 'floor');
  const admin = visible.filter((entry) => entry.group === 'admin');

  const navSection = (label: string, entries: NavEntry[]) =>
    entries.length === 0 ? null : (
      <div className="mb-6">
        <div className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink-faint/70">
          {label}
        </div>
        <nav className="flex flex-col gap-1">
          {entries.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent/12 text-accent-strong shadow-[inset_2px_0_0_0_var(--color-accent)]'
                    : 'text-ink-soft hover:bg-hover hover:text-ink',
                )
              }
            >
              <entry.icon size={18} className="shrink-0" />
              {entry.label}
            </NavLink>
          ))}
        </nav>
      </div>
    );

  const sidebar = (
    <div className="flex h-full flex-col bg-surface">
      <div className="border-b border-line px-4 py-5">
        <Brand />
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-5">
        {navSection('Service', floor)}
        {navSection('Administration', admin)}
      </div>
      <div className="border-t border-line p-4">
        <div className="mb-3">
          <div className="truncate text-sm font-medium text-ink">{user?.fullName}</div>
          <div className="text-xs text-ink-faint">{user?.role}</div>
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="flex w-full items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm text-ink-soft transition hover:border-danger/40 hover:text-danger"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-full bg-canvas">
      {/* Persistent rail on desktop and landscape tablets. */}
      <aside className="no-print hidden w-60 shrink-0 border-r border-line lg:block">{sidebar}</aside>

      {/* Slide-over for narrow / portrait devices. */}
      {drawerOpen ? (
        <div className="no-print fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-scrim/45 backdrop-blur-[2px]" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-line shadow-xl">{sidebar}</aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print flex h-16 shrink-0 items-center gap-4 border-b border-line bg-surface/80 px-4 backdrop-blur">
          <button
            type="button"
            className="rounded-lg p-2 text-ink-soft transition hover:bg-hover hover:text-ink lg:hidden"
            onClick={() => setDrawerOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            {drawerOpen ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
          <div className="lg:hidden">
            <Brand compact />
          </div>
          <div className="ml-auto flex items-center gap-5">
            <div className="tnum font-display text-2xl leading-none text-ink">
              {formatClock(new Date(now))}
            </div>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
